import { useState } from 'react';
import { C, SERIF, mono } from '../../theme';
import { Icon } from '../../icons';
import { Loading, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, ApiError } from '../../api';
import type { RoomRow, LedgerStatus } from '../../api';
import { useAsync } from '../../useAsync';
import { inr } from '../../format';
import { SampleBadge } from '../../components/SampleBadge';
import { Chip } from '../../components/Chip';
import type { ChipTone } from '../../components/Chip';
import {
  Sheet, Field, TextInput, MoneyInput, SelectInput, Segmented, ErrorBanner, PrimaryBtn, GhostBtn,
  rupeesToPaise, paiseToRupees,
} from '../forms/Sheet';

const KIND_OPTS = [
  { value: 'room', label: 'Room' },
  { value: 'shop', label: 'Shop' },
  { value: 'office', label: 'Office' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'showroom', label: 'Showroom' },
  { value: 'other', label: 'Other' },
];

function payTone(s?: LedgerStatus | null): { tone: ChipTone; label: string } | null {
  if (!s) return null;
  if (s === 'paid') return { tone: 'paid', label: 'Paid' };
  if (s === 'overdue') return { tone: 'overdue', label: 'Overdue' };
  if (s === 'waived') return { tone: 'neutral', label: 'Waived' };
  return { tone: 'due', label: 'Due' };
}
function stripe(r: RoomRow): string {
  if (r.status === 'out_of_service') return C.line;
  if (!r.occupied) return C.line;
  return r.payStatus === 'overdue' ? C.red : r.payStatus === 'paid' ? C.hi : C.amber;
}

/**
 * Rooms board — the property as a grid of rooms. Rent lives on each room
 * (baseRent); occupancy and pay-status come from the ledger. Owners set a room's
 * asking rent here before any tenant, and take rooms off the market.
 */
export function RoomsPage({ propertyId, propertyName, onOpen }: { propertyId?: string; propertyName?: string; onOpen: (tenancyId: string) => void }) {
  const { data, loading, error, reload } = useAsync<RoomRow[]>(
    () => (propertyId ? api.rooms(propertyId) : Promise.resolve([])),
    [propertyId],
  );
  const [edit, setEdit] = useState<RoomRow | 'new' | null>(null);

  if (!propertyId) return <Empty icon="grid" title="No property yet" minHeight={420} />;
  if (loading && !data) return <Loading label="Loading rooms…" minHeight={420} />;
  if (error) {
    return (
      <Empty icon="grid" title="Rooms unavailable" hint="We couldn't load the rooms board." minHeight={420}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  }

  const rooms = data ?? [];
  const occupied = rooms.filter((r) => r.occupied).length;
  const potential = rooms.reduce((s, r) => s + (r.occupied ? (r.rentAmount ?? 0) : (r.baseRent ?? 0)), 0);
  const current = rooms.reduce((s, r) => s + (r.occupied ? (r.rentAmount ?? 0) : 0), 0);

  // Group by floor, preserving first-seen order.
  const floors: Array<{ floor: string; rooms: RoomRow[] }> = [];
  for (const r of rooms) {
    const f = r.floor?.trim() || 'Unassigned';
    let g = floors.find((x) => x.floor === f);
    if (!g) { g = { floor: f, rooms: [] }; floors.push(g); }
    g.rooms.push(r);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Rooms</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {propertyName ?? 'Property'}{data ? ` · ${occupied} of ${rooms.length} occupied · ${inr(current)} of ${inr(potential)} roll` : ''}
          </div>
        </div>
        <button onClick={() => setEdit('new')} style={primaryBtn}><Icon n="plus" size={15} color="#fff" /> Add room</button>
      </div>

      {rooms.length === 0 ? (
        <Empty icon="key" title="No rooms yet" hint="Add rooms and set each one's rent, floor by floor." minHeight={300}>
          <button onClick={() => setEdit('new')} style={retryBtn}>Add your first room</button>
        </Empty>
      ) : (
        floors.map((g) => {
          const roll = g.rooms.reduce((s, r) => s + (r.occupied ? (r.rentAmount ?? 0) : (r.baseRent ?? 0)), 0);
          return (
            <div key={g.floor}>
              <div style={{ fontSize: 11.5, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, fontWeight: 600, marginBottom: 9 }}>
                {g.floor}{roll > 0 ? ` · ${inr(roll)}` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 11 }}>
                {g.rooms.map((r) => {
                  const tone = payTone(r.payStatus);
                  const rent = r.occupied ? r.rentAmount : r.baseRent;
                  const oos = r.status === 'out_of_service';
                  return (
                    <div
                      key={r.id}
                      onClick={() => (r.occupied && r.tenancyId ? onOpen(r.tenancyId) : setEdit(r))}
                      style={{ position: 'relative', background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: '13px 14px 13px 17px', cursor: 'pointer' }}
                    >
                      <span style={{ position: 'absolute', left: 0, top: 12, bottom: 12, width: 3, borderRadius: 3, background: stripe(r) }} />
                      <button onClick={(e) => { e.stopPropagation(); setEdit(r); }} aria-label="Edit room" title="Edit room" style={{ position: 'absolute', top: 10, right: 10, border: 0, background: 'transparent', color: C.muted, cursor: 'pointer', padding: 2, display: 'flex' }}>
                        <Icon n="gear" size={14} />
                      </button>
                      <div style={{ ...mono(), fontWeight: 700, fontSize: 14, color: C.ink }}>{r.label}<SampleBadge when={r.isSample} />{r.roomNumber ? <span style={{ color: C.muted, fontWeight: 400 }}> · {r.roomNumber}</span> : null}</div>
                      <div style={{ ...mono(), fontSize: 12.5, color: rent != null ? C.ink2 : C.muted, marginTop: 3 }}>{rent != null ? `${inr(rent)}/mo` : 'Rent not set'}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {oos ? 'Out of service' : r.occupied ? r.tenantName : 'Vacant'}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        {r.occupied && tone && !oos ? (
                          <Chip tone={tone.tone} size="sm">{tone.label}</Chip>
                        ) : (
                          <Chip tone="neutral" size="sm">{oos ? 'Off market' : 'Vacant'}</Chip>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {edit && (
        <RoomSheet
          propertyId={propertyId}
          room={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); reload(); }}
        />
      )}
    </div>
  );
}

function RoomSheet({ propertyId, room, onClose, onSaved }: { propertyId: string; room: RoomRow | null; onClose: () => void; onSaved: () => void }) {
  const notify = useToast();
  const editing = !!room;
  const [label, setLabel] = useState(room?.label ?? '');
  const [floor, setFloor] = useState(room?.floor ?? '');
  const [roomNumber, setRoomNumber] = useState(room?.roomNumber ?? '');
  const [kind, setKind] = useState(room?.kind ?? 'room');
  const [rent, setRent] = useState(paiseToRupees(room?.baseRent));
  const [deposit, setDeposit] = useState(paiseToRupees(room?.deposit));
  const [oos, setOos] = useState(room?.status === 'out_of_service');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const occupied = !!room?.occupied;

  async function save() {
    setError(null);
    if (!label.trim()) return setError('Give the room a code (e.g. GF01).');
    setBusy(true);
    try {
      const base = {
        label: label.trim(),
        floor: floor.trim() || null,
        roomNumber: roomNumber.trim() || null,
        kind,
        baseRent: rupeesToPaise(rent),
        deposit: rupeesToPaise(deposit),
        status: (oos ? 'out_of_service' : 'available') as 'available' | 'out_of_service',
      };
      if (editing && room) {
        await api.updateUnit(room.id, base);
      } else {
        await api.createUnit({ propertyId, ...base, floor: base.floor ?? undefined, roomNumber: base.roomNumber ?? undefined, baseRent: base.baseRent ?? undefined, deposit: base.deposit ?? undefined });
      }
      notify(editing ? 'Room saved' : 'Room added');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the room.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      icon="key"
      title={editing ? 'Edit room' : 'Add room'}
      subtitle={room?.label}
      onClose={onClose}
      busy={busy}
      footer={<><GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn><PrimaryBtn onClick={save} busy={busy}>{editing ? 'Save room' : 'Add room'}</PrimaryBtn></>}
    >
      <ErrorBanner message={error} />
      {occupied && (
        <div style={{ fontSize: 12.5, color: C.ink2, background: C.soft, borderRadius: 9, padding: '9px 11px' }}>
          Occupied by <b>{room?.tenantName}</b>. Editing the room's asking rent won't change the current lease's rent.
        </div>
      )}
      <Field label="Room code" required><TextInput value={label} onChange={setLabel} placeholder="e.g. GF01" autoFocus /></Field>
      <Field label="Floor"><TextInput value={floor} onChange={setFloor} placeholder="e.g. Ground Floor" /></Field>
      <Field label="Room number"><TextInput value={roomNumber} onChange={setRoomNumber} placeholder="e.g. 1" /></Field>
      <Field label="Kind"><SelectInput value={kind} onChange={setKind} options={KIND_OPTS} /></Field>
      <Field label="Asking rent / month"><MoneyInput value={rent} onChange={setRent} placeholder="0" /></Field>
      <Field label="Deposit (default)"><MoneyInput value={deposit} onChange={setDeposit} placeholder="0" /></Field>
      <Field label="Availability">
        <Segmented value={oos ? 'oos' : 'available'} onChange={(v) => setOos(v === 'oos')} options={[{ value: 'available', label: 'Available' }, { value: 'oos', label: 'Out of service' }]} />
      </Field>
    </Sheet>
  );
}

const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 7, background: C.accent, color: '#fff', border: 0, borderRadius: 9, padding: '9px 14px', fontWeight: 600, fontSize: 13.3, cursor: 'pointer' };
const retryBtn: React.CSSProperties = { marginTop: 4, border: `1px solid ${C.line}`, background: C.card, color: C.accent, fontWeight: 600, fontSize: 13, padding: '8px 16px', borderRadius: 9, cursor: 'pointer' };
